import { computed, type ComputedRef, type ToRefs } from "vue";
import type { SelectTriggerProps } from "./select.types";
import { useSelect, getItemLabel } from "./useSelect";
import { selectTriggerClasses } from "./select.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { cardClasses } from "components/Card/card.styles";
import { useTagReorder, type ReorderTagBindings, type ReorderContainerBindings } from "composables/useTagReorder";
import type { AcceptableValue } from "@/runtime/types/utils";

export interface UseSelectTriggerOptions {
  props: Readonly<SelectTriggerProps>;
  state: ToRefs<SelectTriggerProps>;
}

export interface UseSelectTriggerReturn {
  /** Whether Select is in multiple mode */
  isMultiple: ComputedRef<boolean>;

  /** Array of selected tags (for multiple mode) */
  tags: ComputedRef<AcceptableValue[]>;

  /** Get display label for a tag value */
  getTagDisplayValue: (value: AcceptableValue) => string;

  /** Remove a tag from selection */
  removeTag: (value: AcceptableValue) => void;

  /** Resolved placeholder text */
  placeholder: ComputedRef<string>;

  /** Display text for trigger (single mode) */
  displayText: ComputedRef<string>;

  /** Whether currently showing placeholder */
  isPlaceholder: ComputedRef<boolean>;

  /** Computed CSS classes for trigger */
  classes: ComputedRef<ReturnType<typeof selectTriggerClasses>>;

  /** Click handler */
  handleClick: () => void;

  /** Keydown handler */
  handleKeydown: (event: KeyboardEvent) => void;

  /** Tabindex: -1 when disabled, 0 otherwise */
  tabindex: ComputedRef<number>;

  /** Drag-reorder bindings per tag index */
  tagBindings: (index: number) => ReorderTagBindings;

  /** Drag-reorder bindings on the trigger container (handles drop) */
  reorderContainerBindings: () => ReorderContainerBindings;
}

const computeTriggerClasses = (
  select: ReturnType<typeof useSelect>,
  isMultiple: ComputedRef<boolean>,
  propsClass: string | undefined,
) => {
  const variantClasses = selectTriggerClasses({
    variant: select.finalVariant.value,
    size: select.finalSize.value,
    open: select.isOpen.value ? "true" : "false",
    disabled: select.selectProps.value?.disabled ? "true" : "false",
    multiple: isMultiple.value ? "true" : "false",
    block: select.selectProps.value?.block ? "true" : "false",
  });
  const card = cardClasses({
    color: select.finalColor.value,
    variant: select.finalVariant.value,
    size: select.finalSize.value,
  });
  return {
    ...variantClasses,
    base: mergeClassProp(`${card} ${variantClasses.base}`.trim(), propsClass),
  };
};

const handleTriggerKeydown = (event: KeyboardEvent, select: ReturnType<typeof useSelect>): void => {
  if (select.selectProps.value?.disabled) return;

  if (select.isOpen.value) {
    if (event.key === "Escape") {
      event.preventDefault();
      select.close();
    }
    return;
  }

  switch (event.key) {
    case "Enter":
    case " ":
    case "ArrowDown": {
      event.preventDefault();
      select.open();
      break;
    }
  }
};

const buildTriggerComputeds = (select: ReturnType<typeof useSelect>, props: Readonly<SelectTriggerProps>) => {
  const isMultiple = computed(() => select.selectProps.value?.multiple ?? false);
  const tags = computed((): AcceptableValue[] => {
    if (!isMultiple.value) return [];
    const value = select.modelValue.value;
    return Array.isArray(value) ? value : [];
  });
  const placeholder = computed(() => props.placeholder ?? select.selectProps.value?.placeholder ?? "Select...");
  const displayText = computed(() => {
    if (isMultiple.value) return tags.value.length === 0 ? placeholder.value : "";
    return select.displayValue.value || placeholder.value;
  });
  const isPlaceholder = computed(() => (isMultiple.value ? tags.value.length === 0 : !select.displayValue.value));
  const tabindex = computed(() => (select.selectProps.value?.disabled ? -1 : 0));
  const classes = computed(() => computeTriggerClasses(select, isMultiple, props.class));
  return { isMultiple, tags, placeholder, displayText, isPlaceholder, tabindex, classes };
};

export const useSelectTrigger = (options: UseSelectTriggerOptions): UseSelectTriggerReturn => {
  const { props } = options;
  const select = useSelect();
  const computeds = buildTriggerComputeds(select, props);

  const getTagDisplayValue = (value: AcceptableValue): string => {
    const selectEl = select.selectElement.value;
    if (selectEl) {
      const label = getItemLabel(selectEl, value);
      if (label) return label;
    }
    return String(value);
  };

  const removeTag = (value: AcceptableValue) => {
    if (select.selectProps.value?.disabled) return;
    select.deselect(value);
  };

  const handleClick = () => {
    if (select.selectProps.value?.disabled) return;
    select.toggle();
  };

  const sortable = computed(() => select.selectProps.value?.sortable);
  const { tagBindings, containerBindings: reorderContainerBindings } = useTagReorder({
    sortable,
    tags: computeds.tags,
    onReorder: (next) => select.setModelValue(next),
  });

  return {
    ...computeds,
    getTagDisplayValue,
    removeTag,
    handleClick,
    handleKeydown: (event: KeyboardEvent) => handleTriggerKeydown(event, select),
    tagBindings,
    reorderContainerBindings,
  };
};
